import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { Box, type BoxProps, type SvgIconProps, Tooltip } from "@mui/material";
import type { ComponentType } from "react";

/**
 * Accessible status primitive.
 *
 * The legacy dots across the app conveyed status by COLOUR ALONE — a
 * filled circle whose hue was the only signal (WCAG 1.4.1 failure).
 * This primitive keeps the same compact footprint but adds two extra
 * channels so the status no longer depends on colour perception:
 *
 *   1. a DISTINCT GLYPH per tone (shape cue) — check / cross / triangle
 *      / open ring / filled dot — so the states are told apart in
 *      greyscale or by colour-blind users;
 *   2. an ACCESSIBLE NAME exposed to assistive tech via `role="img"` +
 *      `aria-label`, and surfaced visually on hover through the same
 *      `Tooltip` pattern the dots already used.
 *
 * Tones map onto theme palette slots that are already in use by the
 * existing dot components (`success` / `warning` / `error` / `primary`
 * / a neutral grey), so no new colour tokens are introduced and the
 * AA-contrast characteristics of those slots are inherited.
 */
export type StatusTone = "success" | "warning" | "error" | "active" | "neutral";

const TONE_CONFIG: Record<
  StatusTone,
  { color: string; Icon: ComponentType<SvgIconProps> }
> = {
  // Filled check — a finished / passing state.
  success: { color: "success.main", Icon: CheckCircleRoundedIcon },
  // Filled triangle — caution / needs attention.
  warning: { color: "warning.main", Icon: WarningRoundedIcon },
  // Filled circle with cross — a hard failure / error.
  error: { color: "error.main", Icon: ErrorRoundedIcon },
  // Filled dot — an in-flight / structural "live" state (trace cyan).
  active: { color: "primary.main", Icon: CircleRoundedIcon },
  // Open ring — an empty / not-yet / draft state.
  neutral: { color: "text.disabled", Icon: RadioButtonUncheckedRoundedIcon },
};

interface StatusIndicatorProps {
  /** Semantic tone driving both the colour and the glyph shape. */
  tone: StatusTone;
  /** Accessible name describing what the status means (e.g. "Passed"). */
  label: string;
  /** Glyph size in px. Defaults to 14 (roughly the old dot footprint). */
  size?: number;
  sx?: BoxProps["sx"];
}

/**
 * Renders a status as a colour + shape + accessible name. The meaning is
 * always available to assistive tech (`role="img"` + `aria-label`) and
 * shown on hover via a tooltip.
 */
export function StatusIndicator({
  tone,
  label,
  size = 14,
  sx,
}: StatusIndicatorProps) {
  const { color, Icon } = TONE_CONFIG[tone];
  return (
    <Tooltip title={label} arrow placement="top">
      <Box
        component="span"
        role="img"
        aria-label={label}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          ...sx,
        }}
      >
        <Icon sx={{ fontSize: size, color }} />
      </Box>
    </Tooltip>
  );
}
